import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { isFeatureEnabled } from '../types/business';
import type { PickupRequest, PickupRequestStatus } from '../types/pickupRequest';
import { getPickupPublicStatusLabel } from '../types/pickupRequest';

function mapPickupDoc(id: string, data: any): PickupRequest {
  const status = data.status as PickupRequestStatus;
  return {
    id,
    businessId: data.businessId,
    trackingId: data.trackingId,
    source: data.source,
    status,
    name: data.name,
    phone: data.phone,
    email: data.email,
    city: data.city,
    district: data.district,
    address: data.address,
    requestedDate: data.requestedDate,
    requestedTimeSlot: data.requestedTimeSlot,
    message: data.message,
    scheduledDate: data.scheduledDate,
    scheduledTime: data.scheduledTime,
    appointmentId: data.appointmentId,
    clientId: data.clientId,
    assignedEmployeeId: data.assignedEmployeeId,
    assignedEmployeeName: data.assignedEmployeeName,
    siteId: data.siteId,
    siteName: data.siteName,
    transactionGroupId: data.transactionGroupId,
    paymentPlanId: data.paymentPlanId,
    prestationIds: data.prestationIds,
    publicSnapshot: {
      businessPublicName: data.publicSnapshot?.businessPublicName || '',
      statusLabel:
        data.publicSnapshot?.statusLabel ||
        getPickupPublicStatusLabel(status) ||
        status,
      updatedAt:
        data.publicSnapshot?.updatedAt?.toDate?.() ||
        data.updatedAt?.toDate?.() ||
        new Date(),
    },
    createdAt: data.createdAt?.toDate?.() || new Date(),
    updatedAt: data.updatedAt?.toDate?.() || new Date(),
    processedAt: data.processedAt?.toDate?.(),
    processedBy: data.processedBy,
    rejectionReason: data.rejectionReason,
  };
}

export function usePickupRequests(statusFilter?: PickupRequestStatus | 'all') {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;
  const featureOn = isFeatureEnabled(currentBusiness, 'pickup');
  const canView = featureOn && (permissions?.canViewPickups ?? false);

  return useQuery({
    queryKey: ['pickupRequests', businessId, statusFilter || 'all'],
    enabled: !!businessId && canView,
    queryFn: async () => {
      if (!businessId) return [];
      const constraints: any[] = [
        where('businessId', '==', businessId),
        orderBy('createdAt', 'desc'),
        limit(100),
      ];
      if (statusFilter && statusFilter !== 'all') {
        constraints.splice(1, 0, where('status', '==', statusFilter));
      }
      const q = query(collection(db, COLLECTIONS.pickupRequests), ...constraints);
      const snap = await getDocs(q);
      return snap.docs.map((d) => mapPickupDoc(d.id, d.data()));
    },
  });
}

export function usePickupRequestsPendingCount(options?: { enabled?: boolean }) {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;
  const featureOn = isFeatureEnabled(currentBusiness, 'pickup');
  const canView = featureOn && (permissions?.canViewPickups ?? false);
  const enabled =
    options?.enabled !== undefined
      ? options.enabled && !!businessId
      : !!businessId && canView;

  return useQuery({
    queryKey: ['pickupRequests', businessId, 'pending-count'],
    enabled,
    queryFn: async () => {
      if (!businessId) return 0;
      const q = query(
        collection(db, COLLECTIONS.pickupRequests),
        where('businessId', '==', businessId),
        where('status', '==', 'pending'),
        limit(50)
      );
      const snap = await getDocs(q);
      return snap.size;
    },
    refetchInterval: enabled ? 60_000 : false,
  });
}

export function useUpdatePickupStatus() {
  const { user, currentBusiness } = useAuth();
  const permissions = usePermissions();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      id: string;
      status: PickupRequestStatus;
      rejectionReason?: string;
      transactionGroupId?: string;
      paymentPlanId?: string;
      prestationIds?: string[];
      clientId?: string;
      scheduledDate?: string;
      scheduledTime?: string;
      appointmentId?: string;
      assignedEmployeeId?: string;
      assignedEmployeeName?: string;
    }) => {
      if (!currentBusiness?.id) {
        throw new Error('Business non défini');
      }
      const isSaleLink =
        !!input.transactionGroupId || !!input.paymentPlanId || !!input.prestationIds;
      if (isSaleLink) {
        if (!permissions?.canValidatePickup && !permissions?.canUpdatePickupStatus) {
          throw new Error('Permission insuffisante pour lier la vente');
        }
      } else if (!permissions?.canUpdatePickupStatus) {
        throw new Error('Permission insuffisante pour changer le statut');
      }
      if (input.status === 'rejected' && !String(input.rejectionReason || '').trim()) {
        throw new Error('Motif de refus requis');
      }

      const ref = doc(db, COLLECTIONS.pickupRequests, input.id);
      const patch: Record<string, unknown> = {
        status: input.status,
        updatedAt: serverTimestamp(),
        processedAt: serverTimestamp(),
        processedBy: user?.uid || null,
        'publicSnapshot.statusLabel': getPickupPublicStatusLabel(input.status),
        'publicSnapshot.updatedAt': serverTimestamp(),
      };

      if (input.status === 'rejected') {
        patch.rejectionReason = String(input.rejectionReason).trim();
      }
      if (input.transactionGroupId) {
        patch.transactionGroupId = input.transactionGroupId;
      }
      if (input.paymentPlanId) {
        patch.paymentPlanId = input.paymentPlanId;
      }
      if (input.prestationIds) {
        patch.prestationIds = input.prestationIds;
      }
      if (input.clientId) {
        patch.clientId = input.clientId;
      }
      if (input.scheduledDate) {
        patch.scheduledDate = input.scheduledDate;
      }
      if (input.scheduledTime) {
        patch.scheduledTime = input.scheduledTime;
      }
      if (input.appointmentId) {
        patch.appointmentId = input.appointmentId;
      }
      if (input.assignedEmployeeId) {
        patch.assignedEmployeeId = input.assignedEmployeeId;
      }
      if (input.assignedEmployeeName) {
        patch.assignedEmployeeName = input.assignedEmployeeName;
      }

      await updateDoc(ref, patch as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pickupRequests'] });
    },
  });
}
